import { LitElement, html, css, PropertyValueMap } from "lit";
import { customElement, property } from "lit/decorators.js";
import { font } from "@/util/fonts";
import { createRef, ref } from "lit/directives/ref.js";
import { SlInput } from "@shoelace-style/shoelace";

@customElement("fix-input")
export class FixInput extends LitElement {
  @property({ type: String, reflect: true })
  name = "";

  @property({ type: String, reflect: true })
  type = undefined;

  @property({ type: String, reflect: true })
  placeholder = undefined;

  @property({ type: Boolean, reflect: true })
  required = false;

  @property({ type: Boolean, reflect: true })
  size = "large";

  @property({ type: Boolean, reflect: true })
  clearable = false;

  @property({ type: String, reflect: true })
  helpText = null;

  @property({ type: String, reflect: true })
  value = "";

  @property({ type: String })
  customError = "";

  internals: ElementInternals;

  private ref = createRef<SlInput>();

  static get formAssociated() {
    return true;
  }

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.firstUpdated(_changedProperties);
    this.internals.setFormValue(this.value);
  }

  protected async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
    super.updated(_changedProperties);
    await this.updateComplete;

    const input = this.ref.value!;
    if (this.customError) {
      input.setCustomValidity(this.customError);
    } else {
      input.setCustomValidity("");
    }
  }

  render() {
    return html`
      <sl-input
        ${ref(this.ref)}
        name="${this.name}"
        type="${this.type}"
        placeholder="${this.placeholder}"
        required=${this.required}
        size="${this.size}"
        clearable="${this.clearable}"
        .value="${this.value}"
        @sl-change=${this.changeHandler}
      >
        <span slot="clear-icon" class="clear-icon">
          <sl-icon slot="clear-icon" name="x-circle-fill" library="system" aria-hidden="true"></sl-icon>
        </span>
        <span slot="help-text" class="help-text">${this.helpMessage}</span>
      </sl-input>
    `;
  }

  get helpMessage() {
    return this.customError ? this.customError : this.helpText;
  }

  changeHandler = (event: { target: { value: string; validity: ValidityState } }) => {
    this.value = event.target.value;

    const input = this.ref.value!;
    input.setCustomValidity("");
    if (this.customError) {
      this.customError = "";
    }

    this.internals.setFormValue(this.value);

    this.internals.setValidity(event.target.validity, this.helpText ?? "");
  };

  static styles = css`
    sl-input::part(input)::placeholder {
      ${font("Base/Normal")}
    }
    sl-input::part(base) {
      border-radius: var(--components-input-global-border-radius-lg, 8px);
      border: var(--components-input-global-line-width, 1px) solid var(--components-input-global-color-border, #d9d9d9);
      background: var(--components-input-global-color-bg-container, #ffffff);
    }
    sl-input::part(input) {
      ${font("LG/Normal")}
    }

    /* user invalid styles */
    sl-input[data-user-invalid]::part(base) {
      border-color: var(--Components-Input-Global-colorError, #ff4d4f);
    }

    sl-input[data-user-invalid]::part(form-control-label) {
      color: var(--Components-Input-Global-colorError, #ff4d4f);
    }

    sl-input:focus-within[data-user-invalid]::part(base) {
      border-color: var(--Components-Input-Global-colorError, #ff4d4f);
      box-shadow: 0 0 0 var(--sl-focus-ring-width) var(--sl-color-danger-300);
    }

    sl-input .clear-icon {
      display: none;
    }

    sl-input .help-text {
      display: none;
    }
    .help-text {
      ${font("Base/Normal")}
    }

    sl-input[data-user-invalid] .clear-icon,
    sl-input:focus-within[data-user-invalid] .clear-icon {
      display: flex;
      align-content: center;
      color: var(--Components-Input-Global-colorError, #ff4d4f);
    }

    sl-input[data-user-invalid] .help-text,
    sl-input:focus-within[data-user-invalid] .help-text {
      display: flex;
      color: var(--Components-Input-Global-colorError, #ff4d4f);
    }
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    "fix-input": FixInput;
  }
}
